import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Role-based access control hook
 * Provides utilities to check user permissions and role-based access
 */
export const useRoleAccess = () => {
  const authContext = useAuth();
  
  // Safeguard: Return default values if auth context is not available
  if (!authContext) {
    return {
      userRole: 'job_seeker',
      hasRole: () => false,
      hasAnyRole: () => false,
      hasMinimumRole: () => false,
      canAccessJobSeekerFeatures: () => false,
      canAccessEmployerFeatures: () => false,
      canManageTeam: () => false,
      canManageJobs: () => false,
      canViewCandidates: () => false,
      canApplyToJobs: () => false,
      getAllowedRoutes: () => [],
      canAccessRoute: () => false,
      isJobSeeker: false,
      isHiringManager: false,
      isCompanyAdmin: false,
      isAdmin: false
    };
  }

  const { user, userProfile } = authContext;

  const userRole = useMemo(() => {
    return userProfile?.role || user?.user_metadata?.role || 'job_seeker';
  }, [userProfile, user]);

  // Role hierarchy: admin > company_admin > hiring_manager > job_seeker
  const roleHierarchy = {
    admin: 4,
    company_admin: 3,
    hiring_manager: 2,
    job_seeker: 1
  };

  /**
   * Check if user has specific role
   */
  const hasRole = (role) => {
    return userRole === role;
  };

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (roles = []) => {
    return roles?.includes(userRole);
  };

  /**
   * Check if user has at least the minimum role level
   */
  const hasMinimumRole = (minimumRole) => {
    const userLevel = roleHierarchy?.[userRole] || 0;
    const requiredLevel = roleHierarchy?.[minimumRole] || 0;
    return userLevel >= requiredLevel;
  };

  /**
   * Check if user can access job seeker features
   */
  const canAccessJobSeekerFeatures = () => {
    return hasAnyRole(['job_seeker']);
  };

  /**
   * Check if user can access employer features
   */
  const canAccessEmployerFeatures = () => {
    return hasAnyRole(['hiring_manager', 'company_admin', 'admin']);
  };

  /**
   * Check if user can manage team
   */
  const canManageTeam = () => {
    return hasAnyRole(['company_admin', 'admin']);
  };

  /**
   * Check if user can manage jobs
   */
  const canManageJobs = () => {
    return hasAnyRole(['hiring_manager', 'company_admin', 'admin']);
  };

  /**
   * Check if user can view candidates
   */
  const canViewCandidates = () => {
    return hasAnyRole(['hiring_manager', 'company_admin', 'admin']);
  };

  /**
   * Check if user can apply to jobs
   */
  const canApplyToJobs = () => {
    return hasRole('job_seeker');
  };

  /**
   * Get allowed routes based on user role
   */
  const getAllowedRoutes = () => {
    const routes = {
      job_seeker: [
        '/dashboard',
        '/dashboard/jobs',
        '/dashboard/saved-jobs',
        '/dashboard/applications',
        '/dashboard/messages',
        '/dashboard/interviews',
        '/dashboard/notifications',
        '/dashboard/profile',
        '/jobs',
        '/jobs/:id'
      ],
      hiring_manager: [
        '/company-dashboard-hub',
        '/company-dashboard-hub/:companyId',
        '/employer-dashboard',
        '/jobs',
        '/jobs/:id'
      ],
      company_admin: [
        '/company-dashboard-hub',
        '/company-dashboard-hub/:companyId',
        '/company-team-management',
        '/employer-dashboard',
        '/jobs',
        '/jobs/:id'
      ],
      admin: [
        // Admin has access to all routes
        '*'
      ]
    };

    return routes?.[userRole] || routes?.job_seeker;
  };

  /**
   * Check if user can access a specific route
   */
  const canAccessRoute = (route) => {
    const allowedRoutes = getAllowedRoutes();
    
    // Admin has access to all routes
    if (allowedRoutes?.includes('*')) return true;
    
    // Check exact match
    if (allowedRoutes?.includes(route)) return true;
    
    // Check pattern match (e.g., /jobs/:id)
    return allowedRoutes?.some(allowedRoute => {
      if (!allowedRoute?.includes(':')) return false;
      
      const pattern = allowedRoute?.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      return regex?.test(route);
    });
  };

  return {
    userRole,
    hasRole,
    hasAnyRole,
    hasMinimumRole,
    canAccessJobSeekerFeatures,
    canAccessEmployerFeatures,
    canManageTeam,
    canManageJobs,
    canViewCandidates,
    canApplyToJobs,
    getAllowedRoutes,
    canAccessRoute,
    isJobSeeker: userRole === 'job_seeker',
    isHiringManager: userRole === 'hiring_manager',
    isCompanyAdmin: userRole === 'company_admin',
    isAdmin: userRole === 'admin'
  };
};

export default useRoleAccess;